// src/hooks/usePushNotifications.js
// Push Notifications for iOS using Capacitor + Firebase
// 🔥 V3: Uses Firebase Messaging to get token directly

import { useEffect, useCallback, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { doc, setDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';

const usePushNotifications = () => {
    const { user } = useAuth();
    const initialized = useRef(false);

    // Save token to Firestore
    const saveTokenToFirebase = useCallback(async (token) => {
        if (!user?.uid || !token) {
            console.log('🔔 Cannot save token - no user or token');
            return;
        }

        try {
            console.log('🔔 Saving token to Firebase for user:', user.uid);
            console.log('🔔 Token:', token.substring(0, 30) + '...');
            
            const userRef = doc(db, 'users', user.uid);
            
            await setDoc(userRef, {
                fcmToken: token,
                fcmTokens: arrayUnion(token),
                fcmUpdatedAt: new Date(),
                pushEnabled: true,
                platform: Capacitor.getPlatform(),
            }, { merge: true });

            console.log('✅ FCM Token saved to Firebase!');
        } catch (error) {
            console.error('❌ Failed to save FCM token:', error);
        }
    }, [user?.uid]);

    // Register for push notifications
    const registerPushNotifications = useCallback(async () => {
        console.log('🔔 registerPushNotifications starting...');
        console.log('🔔 Platform:', Capacitor.getPlatform());

        if (Capacitor.getPlatform() !== 'ios') {
            console.log('📱 Not iOS, skipping');
            return;
        }

        try {
            // Step 1: Check/Request permission using Capacitor PushNotifications
            let permStatus = await PushNotifications.checkPermissions();
            console.log('🔔 Permission status:', permStatus.receive);

            if (permStatus.receive === 'prompt') {
                console.log('🔔 Requesting permission...');
                permStatus = await PushNotifications.requestPermissions();
                console.log('🔔 Permission result:', permStatus.receive);
            }

            if (permStatus.receive !== 'granted') {
                console.log('❌ Permission denied');
                return;
            }

            // Step 2: Register with APNs
            console.log('🔔 Registering with APNs...');
            await PushNotifications.register();
            console.log('✅ APNs registration called');

            // Step 3: Get FCM token using Firebase Messaging
            console.log('🔔 Getting FCM token...');
            const result = await FirebaseMessaging.getToken();
            
            if (result.token) {
                console.log('🎫 FCM Token received:', result.token.substring(0, 30) + '...');
                await saveTokenToFirebase(result.token);
            } else {
                console.log('❌ No token received from FirebaseMessaging');
            }

            // Step 4: Setup listener for token refresh
            FirebaseMessaging.addListener('tokenReceived', async (event) => {
                console.log('🔄 Token refreshed:', event.token.substring(0, 30) + '...');
                await saveTokenToFirebase(event.token);
            });

            // Step 5: Setup listener for foreground messages
            FirebaseMessaging.addListener('notificationReceived', (event) => {
                console.log('📬 Notification received:', JSON.stringify(event));
            });

            // Step 6: Setup listener for notification taps
            FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
                console.log('👆 Notification tapped:', JSON.stringify(event));
            });

            console.log('✅ Push notification setup complete!');

        } catch (error) {
            console.error('❌ Push registration error:', error);
            console.error('❌ Error details:', JSON.stringify(error));
        }
    }, [saveTokenToFirebase]);

    // Initialize
    useEffect(() => {
        console.log('🔔 === usePushNotifications useEffect ===');
        console.log('🔔 Platform:', Capacitor.getPlatform());
        console.log('🔔 User:', user?.uid);

        if (Capacitor.getPlatform() !== 'ios') {
            return;
        }

        if (!user?.uid) {
            console.log('🔔 Waiting for user...');
            return;
        }

        if (initialized.current) {
            console.log('🔔 Already initialized');
            return;
        }

        initialized.current = true;
        console.log('🔔 Starting push setup...');
        
        registerPushNotifications();

    }, [user?.uid, registerPushNotifications]);

    const requestPermission = async () => {
        initialized.current = false;
        await registerPushNotifications();
    };

    const disablePush = async () => {
        if (!user?.uid) return;
        const userRef = doc(db, 'users', user.uid);
        await setDoc(userRef, { pushEnabled: false }, { merge: true });
    };

    return { requestPermission, disablePush };
};

export default usePushNotifications;